import { useState, useEffect } from 'react';
import { Box, FormControl, Grid, Typography, InputLabel, Select, MenuItem, Button } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import axiosServices from 'utils/axiosServices';
import { makeFormData, requestUrl, barChartData, sortedPairedDataList, marksColor } from 'utils/Helpers';
import langString from 'utils/langString';
import { useSelector } from 'react-redux';
import { DefaultRootStateProps } from 'types';
import { BarOptions, BarPairedSeries, LineOptions } from 'utils/ChartTypes';

const chartToolbar = {
    show: true,
    offsetX: 0,
    offsetY: 0,
    tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
        customIcons: []
    },
    export: {
        csv: {
            filename: 'AverageMarkComp',
            columnDelimiter: ',',
            headerCategory: 'Student Name',
            headerValue: 'value',
            dateFormatter(timestamp: any) {
                return new Date(timestamp).toDateString();
            }
        },
        svg: {
            filename: 'AverageMarkComp'
        },
        png: {
            filename: 'AverageMarkComp'
        }
    },
    autoSelected: 'zoom'
};

type DashboardChartAllStudentMarksPropsType = {
    batches: any[];
};
export default function DashboardChartAllStudentMarks({ batches }: DashboardChartAllStudentMarksPropsType) {
    const [options, setOptions] = useState<BarOptions | null>(null);
    const [lineOptions, setLineOptions] = useState<LineOptions | null>(null);
    // const [series, setSeries] = useState<BarSeries[]>([]);
    const [series, setSeries] = useState<BarPairedSeries | null>(null);
    const [labels, setLabels] = useState([]);
    const [quizes, setQuizes] = useState<any[]>([]);
    const [quizID, setQuizID] = useState(0);
    const [batchID, setBatchID] = useState(0);
    const [sortBy, setSortBy] = useState('def');
    const [chartType, setChartType] = useState('bar');
    const course = useSelector((state: DefaultRootStateProps) => state.courseData);

    const handleChartChange = (type: string) => {
        setChartType(type);
        setLineOptions({
            chart: {
                height: 'auto',
                type: 'line',
                zoom: {
                    enabled: true
                },
                toolbar: chartToolbar
            },
            // colors: ['#2E93fA'],
            dataLabels: {
                enabled: false
            },
            title: {
                text: 'Mark Comparison',
                align: 'left'
            },
            stroke: {
                curve: 'straight'
            },
            xaxis: {
                categories: labels
            },
            markers: {
                size: 4,
                colors: '#2E93fA',
                strokeColors: '#fff'
            }
        });
    };

    useEffect(() => {
        if (batches && batches.length) {
            setBatchID(batches[0].id);
        }
    }, [batches]);

    useEffect(() => {
        const init = async () => {
            try {
                const response = await axiosServices.post(
                    requestUrl('ace_exam', 'quizes'),
                    makeFormData([
                        { key: 'course_id', value: course?.id },
                        { key: 'batch_id', value: batchID }
                    ])
                );
                if (response.data.success) {
                    if (response.data?.response && response.data?.response.length) {
                        setQuizes(response.data?.response);
                        setQuizID(response.data?.response[0]?.details?.quizid);
                    } else {
                        setQuizes([]);
                    }
                }
            } catch (e) {
                console.log(e);
            }
        };
        if (course.id && batchID) {
            init();
        }
    }, [batchID, course.id]);

    useEffect(() => {
        const init = async () => {
            try {
                const response = await axiosServices.post(
                    requestUrl('dashboard', 'batch_students_mark'),
                    makeFormData([
                        { key: 'batch_id', value: batchID },
                        { key: 'quiz_id', value: quizID || 0 }
                    ])
                );
                if (response.data.success) {
                    if (response.data?.response && response.data?.response.length) {
                        const dataList: [] = barChartData(response.data.response, 'name', 'quizMarkPercentage');
                        const labelsArray = response.data.response.map((item: any) => item.name);
                        setLabels(labelsArray);
                        const sortedPairedData: object[] = sortedPairedDataList(dataList, sortBy);
                        setSeries([{ name: '%', data: sortedPairedData }]);
                        const barColors: string[] = sortedPairedData.map((data: any) => marksColor(parseInt(data.y, 10)));
                        setOptions({
                            chart: {
                                type: 'bar',
                                height: 350,
                                width: 500,
                                toolbar: chartToolbar
                            },
                            colors: barColors,
                            plotOptions: {
                                bar: {
                                    distributed: true,
                                    borderRadius: 4,
                                    horizontal: true,
                                    barHeight: dataList.length < 5 ? '10' : '50'
                                }
                            },
                            title: {
                                text: 'Mark Comparison',
                                align: 'left'
                            },
                            dataLabels: {
                                enabled: false
                            },
                            xaxis: {
                                type: 'category'
                                // categories: labelsArray
                            }
                        });
                    } else {
                        setOptions(null);
                    }
                }
            } catch (e) {
                console.log(e);
            }
        };
        if (batchID && quizID) {
            init();
        }
    }, [batchID, quizID, sortBy]);

    return (
        <Box sx={{ marginY: 3, borderBottom: '1px solid #DDD', padding: 2 }}>
            <Grid container spacing={1}>
                <Grid item sm={3}>
                    <Box sx={{ padding: 1, border: '1px solid #e67e22', borderRadius: '10px', textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ color: '#e67e22' }}>
                            Marks Comparison of All Students for a Quiz
                        </Typography>
                    </Box>
                </Grid>
                <Grid item sm={3}>
                    <FormControl sx={{ paddingRight: 1, paddingLeft: 1, float: 'right', minWidth: '150px' }}>
                        <InputLabel id="batch-select-label">Batch</InputLabel>
                        <Select
                            size="small"
                            name="batchs"
                            labelId="batch-select-label"
                            id="batch-select"
                            value={batchID}
                            label="Batch"
                            onChange={(event: any) => setBatchID(event.target.value)}
                        >
                            {batches.map((item: any) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={3}>
                    <FormControl sx={{ paddingRight: 1, float: 'right', minWidth: '150px' }}>
                        <InputLabel id="quiz-select-label">{langString('quiz')}</InputLabel>
                        <Select
                            size="small"
                            name="quizes"
                            labelId="quiz-select-label"
                            id="quiz-select"
                            value={quizID}
                            label={langString('quiz')}
                            onChange={(event: any) => setQuizID(event.target.value)}
                        >
                            {quizes.map((item: any) => (
                                <MenuItem key={item.details?.quizid} value={item.details?.quizid}>
                                    {item.details?.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={3}>
                    <FormControl sx={{ paddingRight: 1, minWidth: '150px' }}>
                        <InputLabel id="sort-select-label">{langString('sort')}</InputLabel>
                        <Select
                            size="small"
                            name="sorts"
                            labelId="sort-select-label"
                            id="sort-select"
                            value={sortBy}
                            label={langString('sort')}
                            onChange={(event: any) => setSortBy(event.target.value)}
                        >
                            <MenuItem value="def">Default</MenuItem>
                            <MenuItem value="alp">Alphabetical</MenuItem>
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ marginY: 1 }}>
                <Grid item xs={12} marginTop={2}>
                    <Grid container spacing={8} justifyContent="center" alignItems="center">
                        <Grid item>
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: chartType === 'bar' ? '#00807e' : '#cfcfcf' }}
                                onClick={() => handleChartChange('bar')}
                            >
                                Bar Chart
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: chartType === 'line' ? '#00807e' : '#cfcfcf' }}
                                onClick={() => handleChartChange('line')}
                            >
                                Trend Line
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item sm={12} sx={{ display: chartType !== 'bar' ? 'none' : '' }}>
                    {options && series ? (
                        <ReactApexChart
                            options={options}
                            series={series}
                            type="bar"
                            height={labels.length < 17 ? 350 : labels.length * 25}
                        />
                    ) : (
                        <Typography variant="h3" sx={{ textAlign: 'center' }}>
                            {langString('nodatafound')}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} sx={{ display: chartType !== 'line' ? 'none' : '' }}>
                    {lineOptions && series ? (
                        <ReactApexChart options={lineOptions} series={series} type="line" height="auto" />
                    ) : (
                        <Typography variant="h3" sx={{ textAlign: 'center' }}>
                            {langString('nodatafound')}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
